import React from 'react';
import { Card, Skeleton } from 'antd';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  DashboardPageLoader,
  AnalyticsPageLoader,
  ShareLinkPageLoader,
  AntiFraudPageLoader,
  UsersPageLoader,
  EmployeesPageLoader,
  OffersPageLoader,
  OrdersPageLoader,
  ProductBranchesPageLoader,
  RewardsPageLoader,
  EarningRulesPageLoader,
  ReferralsPageLoader,
  PointsPageLoader,
  EmployeePointsPageLoader,
  AdminMenuPageLoader,
  ContentManagementPageLoader,
  FaqManagementPageLoader,
  MealPointsPageLoader,
  AuditLogsPageLoader,
  FraudDetectionPageLoader,
  PointsConfigPageLoader,
  GamificationConfigPageLoader,
  SettingsPageLoader,
  ConfigurationPageLoader,
  CMSPageLoader,
  GamificationPageLoader,
  TermsConfigPageLoader,
  VatCertificatePageLoader,
  CommunicationPageLoader,
  SegmentationPageLoader,
  TiersPageLoader,
  SupportPageLoader,
  UserDetailPageLoader,
  BulkOperationsPageLoader,
  IntegrationsPageLoader,
  WorkflowListPageLoader,
  RulesEngineWorkflowPageLoader,
} from './pageLoaders';

/**
 * Route → dedicated PageLoader component.
 * Each loader is shaped to match its real page UI.
 */
const ROUTE_LOADER_MAP = {
  '/dashboard': DashboardPageLoader,
  '/analytics': AnalyticsPageLoader,
  '/share-link': ShareLinkPageLoader,
  '/anti-fraud': AntiFraudPageLoader,

  '/users': UsersPageLoader,
  '/employees': EmployeesPageLoader,
  '/offers': OffersPageLoader,
  '/orders': OrdersPageLoader,
  '/lyncs/orders': OrdersPageLoader,
  '/marn-admin/branches': ProductBranchesPageLoader,
  '/lyncs/branches': ProductBranchesPageLoader,
  '/rewards': RewardsPageLoader,
  '/earning-rules': EarningRulesPageLoader,
  '/referrals': ReferralsPageLoader,

  '/points': PointsPageLoader,
  '/employee-points': EmployeePointsPageLoader,
  '/product-catalog': AdminMenuPageLoader,
  '/lyncs/product-catalog': AdminMenuPageLoader,
  '/content-management': ContentManagementPageLoader,
  '/faq-management': FaqManagementPageLoader,
  '/meal-points': MealPointsPageLoader,
  '/audit-logs': AuditLogsPageLoader,
  '/fraud-detection': FraudDetectionPageLoader,


  '/points-config': PointsConfigPageLoader,
  '/gamification-config': GamificationConfigPageLoader,
  '/settings': SettingsPageLoader,
  '/configuration': ConfigurationPageLoader,
  '/cms': CMSPageLoader,
  '/gamification': GamificationPageLoader,
  '/terms-config': TermsConfigPageLoader,
  '/common-questions-config': TermsConfigPageLoader,
  '/vat-certificate': VatCertificatePageLoader,
  '/communication': CommunicationPageLoader,
  '/segmentation': SegmentationPageLoader,

  '/tiers': TiersPageLoader,
  '/support': SupportPageLoader,
  '/bulk-operations': BulkOperationsPageLoader,
  '/integrations': IntegrationsPageLoader,
  '/workflows': WorkflowListPageLoader,
  '/rules-engine': RulesEngineWorkflowPageLoader,
};

/**
 * Generic fallback for routes without a dedicated loader.
 */
const GenericLoader = () => (
  <div className="w-full p-4 md:p-6">
    <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
      <Skeleton.Input active size="large" style={{ width: 240, height: 28 }} />
      <Skeleton.Button active size="large" style={{ width: 120 }} />
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {Array.from({ length: 4 }).map((_, i) => (
        <Card key={i} className="shadow-sm">
          <Skeleton active paragraph={{ rows: 2 }} />
        </Card>
      ))}
    </div>
    <Card className="shadow-sm">
      <Skeleton active paragraph={{ rows: 8 }} />
    </Card>
  </div>
);

/**
 * Resolve which loader component to render for a given pathname.
 * - Exact match first
 * - /users/:id → UserDetailPageLoader
 * - Prefix match (e.g. /settings/something → SettingsPageLoader)
 * - Generic fallback
 */
const resolveLoader = (pathname) => {
  if (ROUTE_LOADER_MAP[pathname]) return ROUTE_LOADER_MAP[pathname];

  // User detail pattern
  if (/^\/users\/[^/]+$/.test(pathname)) return UserDetailPageLoader;

  // Prefix match for nested routes
  const prefix = Object.keys(ROUTE_LOADER_MAP).find((k) =>
    pathname.startsWith(k + '/')
  );
  if (prefix) return ROUTE_LOADER_MAP[prefix];

  return GenericLoader;
};

/**
 * PageLoader — dynamic, per-route skeleton matching the real page UI.
 *
 * @param {React.ComponentType} [component] - Explicit loader component override
 * @param {string} [message] - Optional aria-label message
 */
const PageLoader = ({ component: OverrideComponent, message }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const Loader = OverrideComponent || resolveLoader(location.pathname);
  const displayMessage = message || t('Loading...');

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={displayMessage}
      className="min-h-screen"
    >
      <Loader />
    </div>
  );
};

export default PageLoader;
